import { Position } from 'types';
import { Store } from 'stores/type';
import ScrollManager from 'managers/scroll.manager';
import { nextFrame } from 'utils';

class DragManager {
  private $store!: Store;

  public init(store: Store) {
    this.$store = store;
  }

  public onDragStart(id: string | null) {
    if (id === null) return;

    ScrollManager.handler.disable();
    this.$store.dispatch('dragStart', id);
  }
  public onDragEnd(position: Position, simulated = false) {
    ScrollManager.handler.enable();
    this.$store.dispatch('dragEnd', { position, simulated });
  }

  public async onLibraryDistanceDragged(
    elementId: string,
    startPoint: Position,
    dragPoint: Position,
    startPosition: Position,
    startEventType: string,
  ) {
    ScrollManager.handler.disable();

    const position = this.calculatePosition(startPoint, dragPoint, startPosition);
    let id = this.$store.getters.draggableHelperId;
    let helper = this.$store.getters.draggableHelperElement;

    // ensure helper exists
    if (!helper || !this.getWorkspaceBoxElement(id)) {
      this.$store.commit('DRAGGABLE_HELPER_RESET');
      this.$store.dispatch('draggableHelperAdd', true);
      id = this.$store.getters.draggableHelperId;
      helper = this.$store.getters.draggableHelperElement;
      await nextFrame();
    }

    this.$store.commit('DRAGGABLE_HELPER_PREPARE', {
      helper,
      elementId,
      position,
    });
    this.$store.commit('DRAGGABLE_HELPER_RESET');

    this.simulateStartDrag({
      id,
      dragPoint,
      startEventType,
    });
  }
  private calculatePosition(startPoint: Position, dragPoint: Position, startPosition: Position): Position {
    const workspaceSize = this.$store.getters.workspaceBoxDimensions;
    const librarySize = this.$store.getters.libraryBoxDimensions;
    const position = { ...startPosition };
    const margin = this.$store.getters.libraryBoxMargin;

    // check if sizes of image in library and workspace differ - if so then translate position
    const widthDiffer = workspaceSize.width !== librarySize.width;
    const heightDiffer = workspaceSize.height !== librarySize.height;

    if (widthDiffer || heightDiffer) {
      const widthRatio = workspaceSize.width / librarySize.width;
      const heightRatio = workspaceSize.height / librarySize.height;
      const widthTranslate = widthRatio * (startPoint.x - startPosition.x);
      const heightTranslate = heightRatio * (startPoint.y - startPosition.y);
      const dragXTranslate = dragPoint.x - startPoint.x;
      const dragYTranslate = dragPoint.y - startPoint.y;

      position.x = (startPoint.x - widthTranslate) + (widthRatio * margin) + dragXTranslate;
      position.y = (startPoint.y - heightTranslate) + dragYTranslate;
    }

    return position;
  }
  public simulateStartDrag(
    { id, dragPoint: { x, y }, startEventType }: { id: string | null, dragPoint: Position, startEventType: string },
  ) {
    const event = {
      cancelable: false,
      simulated: true,
      pageX: x,
      pageY: y,
      clientX: x,
      clientY: y,
      startEventType,
    };

    const element = this.getWorkspaceBoxElement(id);
    if (!element) {
      throw new Error('DragManager.simulateStartDrag - empty helper');
    }

    const workspaceBox = (element as any).__vue__;
    if (!workspaceBox) return;

    workspaceBox.updateDraggablePosition();
    workspaceBox.draggable.dragStart(event, event);
  }

  private getWorkspaceBoxElement(id: string | null) {
    return document.querySelector(`#workspace [data-id="${ id }"]`);
  }

  get isDragging() {
    return this.$store.getters.isDragging;
  }
}

export default new DragManager();
